import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet.jsx';
import { Button } from '../ui/button.jsx';
import { Skeleton } from '../ui/skeleton.jsx';
import { HISTORY_RETENTION_DAYS, groupHistory } from '../../lib/history';
import { recordHistory } from '../../services/history';
import HistoryGroup from './HistoryGroup';

/**
 * A record's history, in a drawer: its own changes and its children's (a quote
 * request's photos, notes, quote and damage; a customer's vehicles), newest
 * first, grouped so a run of edits reads as one line. A child someone sent to
 * the recycle bin can be restored from here.
 *
 * Opened by RecordHistoryButton. Fetches only while open.
 */
export default function RecordHistoryDrawer({ open, onOpenChange, rowId, title }) {
  const [entries, setEntries] = useState([]);
  const [cursor, setCursor] = useState({ before: null, done: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const seen = useRef(new Set());

  const load = useCallback(async (before, reset) => {
    setLoading(true);
    setError('');
    try {
      if (reset) seen.current = new Set();
      const page = await recordHistory(rowId, { before, seen: seen.current });
      page.entries.forEach((e) => seen.current.add(e.$id));
      setEntries((prev) => (reset ? page.entries : [...prev, ...page.entries]));
      setCursor({ before: page.before, done: page.done });
    } catch (err) {
      console.error('Could not load history:', err);
      setError('History could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [rowId]);

  useEffect(() => {
    if (open && rowId) load(null, true);
  }, [open, rowId, load]);

  const groups = useMemo(() => groupHistory(entries), [entries]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Change history{title ? ` — ${title}` : ''}</SheetTitle>
          <SheetDescription>
            Changes show here a few minutes after they're made, and are kept for {HISTORY_RETENTION_DAYS} days.
          </SheetDescription>
        </SheetHeader>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {!error && loading && entries.length === 0 && (
          <div className="space-y-3" aria-label="Loading history">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        )}

        {!error && !loading && groups.length === 0 && (
          <p className="text-sm text-muted-foreground">No changes recorded yet.</p>
        )}

        {groups.length > 0 && (
          <ul className="divide-y">
            {groups.map((group) => (
              <HistoryGroup
                key={group.key}
                group={group}
                // The record's own lines need no name; a child's do.
                showRecord={group.rowId !== rowId}
                onRestored={() => load(null, true)}
              />
            ))}
          </ul>
        )}

        {!cursor.done && entries.length > 0 && (
          <Button variant="outline" size="sm" onClick={() => load(cursor.before, false)} disabled={loading}>
            {loading ? 'Loading…' : 'Load older'}
          </Button>
        )}
      </SheetContent>
    </Sheet>
  );
}
